/* eslint-disable max-len */
import React from 'react';
import styled from 'styled-components';
import { LFC } from '../Legacy';
import { ApiError } from '../api/ApiClient';
import { Icon } from '../concepts/resources/Icon';
import { Fill } from '../ui/Fill';
import { Colors, remInPx } from './../ui';
import { useResolve } from './Container';
import { AccountModel } from './account/AccountModel';

const Access = styled.div`
    flex: 1 1 auto;
    display: flex;
    padding: 8rem;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 450;
    color: ${Colors.text};
    background: #F9FAFC;

    svg {    
        margin-right: 1rem;
        background: rgba(0,0,0,0.05);
        border-radius: 50%;
        padding: 1rem;
    }
`;

// Don't use <Icon /> in here as it requires resources to load and <Crash /> needs to be used sometimes before we even loaded anything, so embed icons directly.

const DeniedIcon = ({ size }: { size: string }) =>
    <svg width={remInPx(size)} height={remInPx(size)} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>;

const NotFoundIcon = ({ size }: { size: string }) =>
    <svg width={remInPx(size)} height={remInPx(size)} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>;

export const AccessCrash = () =>
    <Access>
        <DeniedIcon size="5" />
        <span>Sorry, you do not have permission<br /> to access this area.</span>
    </Access>;

export const NotFoundCrash = () =>
    <Access>
        <NotFoundIcon size="5" />
        <span>Hmmm, there is nothing here.</span>
    </Access>;

export const NotMemberCrash = () =>
    <Fill flex="column">
        <Access>
            <DeniedIcon size="5" />
            <span style={{ display: 'block', maxWidth: '40rem' }}>
                Sorry, you are not a member of this organization.  Please contact your administrator for access.
            </span>
        </Access>
    </Fill>;

export const NetworkCrash = () =>
    <Access>
        <Icon size="5" src="deqo://icons/hero/exclamation" />
        <div>
            <div>Could not connect to server.</div>
            <div style={{ fontSize: '1rem' }}>Either your connection has been lost or the server is down.</div>
        </div>
    </Access>;

const ApiCrash: LFC<{ error: ApiError, refreshOn403?: boolean }> = ({ error, refreshOn403 }) => {
    const account = useResolve(AccountModel);
    if (error.code === 404) {
        if (error.call.path === '/me.info') {
            return <NotMemberCrash />;
        }
        return <NotFoundCrash />;
    }
    if (error.code === 403) {
        if (refreshOn403) {
            window.location.href = `/${account.id}`;
            return null;
        }
        return <AccessCrash />;
    }
    if (error.code === 600) {
        return <NetworkCrash />;
    }
    return <UnknownCrash error={error} />;
};

const UnknownCrash: LFC<{ error: Error }> = ({ error }) =>
    <Fill flex="column">
        <Access>
            <div>
                <div>{error.name}</div>
                <div>{error.message}</div>
                <div>{error.stack}</div>
            </div>
        </Access>
    </Fill>;

export const Crash: LFC<{ error: Error, refreshOn403?: boolean }> = ({ error, refreshOn403 }) =>
    (error instanceof ApiError)
        ? <ApiCrash error={error} refreshOn403={refreshOn403} />
        : <UnknownCrash error={error} />;
